// マスターシート同期 API クライアント（/api/master 系）

import type { Driver, MonthlyDeduction, SheetMaster } from "../types";
import { noteAuthRequired } from "./authSignal";

export type MasterFetchResult =
  | { ok: true; data: SheetMaster }
  | { ok: false; configured: boolean; error: string };

export async function fetchSheetMaster(): Promise<MasterFetchResult> {
  try {
    const r = await fetch("/api/master");
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      // vite 単体起動など Functions が無い環境 → 未設定扱い（エラー表示しない）
      return { ok: false, configured: false, error: "API未提供（ローカル環境）" };
    }
    const body = (await r.json()) as {
      ok: boolean;
      configured?: boolean;
      error?: string;
      fetchedAt?: string;
      sheetTitle?: string;
      sheetUrl?: string;
      drivers?: Driver[];
      monthlyDeductions?: MonthlyDeduction[];
      warnings?: string[];
    };
    if (!body.ok) {
      return { ok: false, configured: body.configured ?? true, error: body.error || "マスター取得に失敗しました" };
    }
    return {
      ok: true,
      data: {
        fetchedAt: body.fetchedAt!,
        sheetTitle: body.sheetTitle || "",
        sheetUrl: body.sheetUrl || "",
        drivers: body.drivers || [],
        monthlyDeductions: body.monthlyDeductions || [],
        warnings: body.warnings || [],
      },
    };
  } catch (e) {
    // ローカル dev（vite単体）や通信断では masters.json フォールバックで動く
    return { ok: false, configured: true, error: e instanceof Error ? e.message : String(e) };
  }
}

export type AddDriverPayload = {
  name: string;
  org?: string;
  branch?: string;
  email?: string;
  contractFrom?: string;
  unitTakkyu?: number | null;
  unitNekopos?: number | null;
  unitDm?: number | null;
  invoiceNo?: string | null;
  monthlyKeitai?: number | null;
  monthlySharyo?: number | null;
  hasGasCard?: boolean;
  monthlyAppli?: number | null;
  shidoCount?: number | null;
  contractDocOk?: boolean; // 契約書類 受領済み（入社フォーム）
  notes?: string; // 備考（入社フォーム）
  atStyle?: boolean; // AT-STYLE様所属（マスタ「AT-STYLE」列。本城さん要望 2026-08-26）
};

export async function addDriverToSheet(
  payload: AddDriverPayload
): Promise<{ ok: boolean; empId?: string; error?: string; warnings?: string[] }> {
  const r = await fetch("/api/master/add", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
  return (await r.json()) as { ok: boolean; empId?: string; error?: string; warnings?: string[] };
}

export async function retireDriverInSheet(
  empId: string,
  contractTo: string
): Promise<{ ok: boolean; error?: string }> {
  const r = await fetch("/api/master/retire", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ empId, contractTo }),
  });
  noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
  return (await r.json()) as { ok: boolean; error?: string };
}

/**
 * 再稼働（退職処理の取り消し）。契約終了日を空にし、状態が「退職」なら「在職」に戻す。
 * 書き込むのはこの2セルだけ（単価などはシートで直接編集する方針を維持）。
 */
export async function reactivateDriverInSheet(
  empId: string
): Promise<{ ok: boolean; name?: string; clearedContractTo?: string | null; statusRestored?: boolean; error?: string }> {
  const r = await fetch("/api/master/reactivate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ empId }),
  });
  noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
  return (await r.json()) as {
    ok: boolean;
    name?: string;
    clearedContractTo?: string | null;
    statusRestored?: boolean;
    error?: string;
  };
}
